import React from 'react';
import { FaTree, FaLightbulb, FaChartBar } from "react-icons/fa";

export const CommunityDashboard: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card text-center">
          <div className="text-3xl font-bold text-forest-600 mb-2">127</div>
          <div className="text-sm text-gray-600">Trees Planted</div>
          <div className="text-xs text-gray-500 mt-1">+12 this month</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-bold text-blue-600 mb-2">43</div>
          <div className="text-sm text-gray-600">Suggestions</div>
          <div className="text-xs text-gray-500 mt-1">+8 pending review</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-bold text-purple-600 mb-2">89</div>
          <div className="text-sm text-gray-600">Assessments</div>
          <div className="text-xs text-gray-500 mt-1">+15 this week</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-bold text-orange-600 mb-2">156</div>
          <div className="text-sm text-gray-600">Community Members</div>
          <div className="text-xs text-gray-500 mt-1">+23 new members</div>
        </div>
      </div>

      {/* Progress Towards Goals */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">2025 Community Goals</h2>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Trees Planted</span>
              <span className="font-medium">127 / 200</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div className="bg-forest-600 h-3 rounded-full" style={{ width: '63.5%' }}></div>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Site Assessments</span>
              <span className="font-medium">89 / 150</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div className="bg-blue-600 h-3 rounded-full" style={{ width: '59.3%' }}></div>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Community Engagement</span>
              <span className="font-medium">156 / 250</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div className="bg-purple-600 h-3 rounded-full" style={{ width: '62.4%' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity and Suggestions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {[
              {
                type: 'tree_planted',
                user: 'Sarah M.',
                action: 'planted a Red Oak',
                location: 'Main St & 1st Ave',
                time: '2 hours ago',
                icon: <FaTree />
              },
              {
                type: 'suggestion',
                user: 'Mike R.',
                action: 'suggested a location',
                location: 'Central Park entrance',
                time: '5 hours ago',
                icon: <FaLightbulb />
              },
              {
                type: 'assessment',
                user: 'Lisa K.',
                action: 'completed site assessment',
                location: 'River Rd near bridge',
                time: '1 day ago',
                icon: <FaChartBar />
              },
              {
                type: 'tree_planted',
                user: 'Community Group',
                action: 'planted 5 Sugar Maples',
                location: 'Hackensack Elementary',
                time: '2 days ago',
                icon: <FaTree />
              },
            ].map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="text-lg">{activity.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{activity.user}</span> {activity.action}
                  </p>
                  <p className="text-xs text-gray-500">{activity.location}</p>
                  <p className="text-xs text-gray-400">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tree Suggestions */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Pending Suggestions</h3>
            <button className="btn-primary text-sm">
              Add Suggestion
            </button>
          </div>
          <div className="space-y-4">
            {[
              {
                location: 'Park Avenue & 3rd Street',
                user: 'John D.',
                species: 'American Elm',
                score: 85,
                time: '3 hours ago'
              },
              {
                location: 'Community Center parking lot',
                user: 'Maria S.',
                species: 'Red Maple',
                score: 78,
                time: '1 day ago'
              },
              {
                location: 'Library front entrance',
                user: 'David L.',
                species: 'White Oak',
                score: 92,
                time: '2 days ago'
              },
            ].map((suggestion, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{suggestion.location}</h4>
                    <p className="text-xs text-gray-500">
                      Suggested by {suggestion.user} • {suggestion.time}
                    </p>
                  </div>
                  <div className={`
                    px-2 py-1 rounded text-xs font-medium
                    ${suggestion.score >= 80 ? 'bg-green-100 text-green-800' :
                      suggestion.score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }
                  `}>
                    {suggestion.score}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Species: {suggestion.species}</span>
                  <div className="flex space-x-2">
                    <button className="text-xs text-forest-600 hover:text-forest-700">
                      Approve
                    </button>
                    <button className="text-xs text-gray-500 hover:text-gray-600">
                      Review
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Environmental Impact */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Environmental Impact</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 mb-1">2.4 tons</div>
            <div className="text-sm text-gray-600">CO₂ Sequestered Annually</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">18,500 gal</div>
            <div className="text-sm text-gray-600">Stormwater Managed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 mb-1">-3.2°F</div>
            <div className="text-sm text-gray-600">Average Temperature Reduction</div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-forest-50 border border-forest-200 rounded-lg p-6 text-center">
        <h3 className="text-lg font-semibold text-forest-800 mb-2">Get Involved!</h3>
        <p className="text-forest-700 mb-4">
          Help us reach our 2025 goals by participating in community tree planting efforts.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button className="btn-primary">
            Suggest a Location
          </button>
          <button className="btn-secondary">
            Join Next Planting Event
          </button>
        </div>
      </div>
    </div>
  );
};
